import React, { useState, useEffect } from 'react';
import DescriptionTab from './tabs/DescriptionTab';
import SpecsTab from './tabs/SpecsTab';
import HowToBuyTab from './tabs/HowToBuyTab';
import ColorGalleryTab from './tabs/ColorGalleryTab';
import { 
  FaInfoCircle, 
  FaListAlt, 
  FaQuestionCircle, 
  FaPalette, 
  FaStar
} from 'react-icons/fa';

const ProductDetails = ({ product, activeTab, setActiveTab }) => {
  const [hoveredTab, setHoveredTab] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const tabs = [
    { id: 'description', label: 'توضیحات', icon: FaInfoCircle, color: 'from-blue-500 to-blue-600' },
    { id: 'specs', label: 'مشخصات', icon: FaListAlt, color: 'from-emerald-500 to-teal-600' },
    { id: 'howToBuy', label: 'نحوه خرید', icon: FaQuestionCircle, color: 'from-amber-500 to-orange-600' },
    { id: 'colors', label: 'گالری رنگی', icon: FaPalette, color: 'from-purple-500 to-pink-600' },
    { id: 'reviews', label: 'نظرات', icon: FaStar, color: 'from-yellow-500 to-amber-600' },
  ];

  // نام‌های کوتاه شده برای موبایل
  const getTabLabel = (label) => {
    if (!isMobile) return label;
    const shortLabels = {
      'توضیحات': 'توضیحات',
      'مشخصات': 'مشخصات',
      'نحوه خرید': 'خرید',
      'گالری رنگی': 'رنگ‌ها',
      'نظرات': 'نظرات',
    };
    return shortLabels[label] || label;
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
      
      {/* ===== تب‌ها با دیزاین ریسپانسیو ===== */}
      <div className="relative">
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9D9D9]/50" />
        
        <nav 
          className="flex justify-start gap-0.5 sm:gap-1 lg:gap-3 overflow-x-auto overflow-y-hidden scrollbar-hide py-1 flex-nowrap"
          style={{ 
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }}
          aria-label="Tabs"
        >
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isHovered = hoveredTab === tab.id;
            const label = getTabLabel(tab.label);

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                onMouseEnter={() => setHoveredTab(tab.id)}
                onMouseLeave={() => setHoveredTab(null)}
                className={`
                  group relative flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 rounded-xl font-medium transition-all duration-300 flex-shrink-0
                  ${isActive 
                    ? 'text-white shadow-lg scale-[0.98] sm:scale-100 lg:scale-105' 
                    : 'text-[#79716B] hover:text-[#292524] hover:bg-[#F4F1EA]'
                  }
                  ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
                `}
                style={{
                  transitionDelay: `${index * 50}ms`,
                  background: isActive 
                    ? 'linear-gradient(135deg, #9EAD8C, #8A9A7B)' 
                    : 'transparent',
                  boxShadow: isActive 
                    ? '0 4px 15px rgba(158, 173, 140, 0.3)' 
                    : 'none',
                  fontSize: isMobile ? '10px' : '14px',
                  minHeight: isMobile ? '32px' : '44px',
                }}
              >
                {/* ریپل افکت */}
                <span className="absolute inset-0 rounded-xl overflow-hidden">
                  <span className="absolute inset-0 bg-white/20 scale-0 group-active:scale-100 transition-transform duration-500 rounded-xl" />
                </span>

                {/* آیکون */}
                <Icon 
                  className={`
                    transition-all duration-300 flex-shrink-0
                    ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}
                    ${isActive ? 'text-white rotate-0' : isHovered ? 'text-[#9EAD8C] -rotate-6' : 'text-[#79716B]'}
                    group-hover:scale-110
                  `}
                />

                {/* برچسب */}
                <span className="relative whitespace-nowrap text-[10px] sm:text-xs lg:text-sm font-medium">
                  {label}
                  
                  {!isActive && (
                    <span 
                      className={`
                        absolute -bottom-1 left-0 right-0 h-0.5 bg-[#9EAD8C] rounded-full transition-all duration-300
                        ${isHovered ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}
                      `}
                    />
                  )}
                </span>

                {/* شمارنده */}
                {isActive && tab.id === 'colors' && (
                  <span className="text-[8px] sm:text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full text-white">
                    {product.colors?.length || 0}
                  </span>
                )}

                {isActive && tab.id === 'reviews' && (
                  <span className="text-[8px] sm:text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full text-white">
                    ★
                  </span>
                )}

                {/* فلش پایین */}
                {isActive && (
                  <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 sm:w-2.5 sm:h-2.5 rotate-45 bg-[#9EAD8C] rounded-sm" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ===== محتوای تب‌ها ===== */}
      <div className="mt-4 sm:mt-6 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-[#D9D9D9]/30 overflow-hidden transition-all duration-300 hover:shadow-xl">
        <div className="relative p-3 sm:p-4 lg:p-6">
          {/* دکوراسیون پس‌زمینه - مخفی در موبایل */}
          <div className="absolute top-0 right-0 w-20 sm:w-32 h-20 sm:h-32 bg-[#9EAD8C]/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 hidden sm:block" />
          <div className="absolute bottom-0 left-0 w-20 sm:w-32 h-20 sm:h-32 bg-[#8A9A7B]/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2 hidden sm:block" />
          
          {/* محتوای اصلی */}
          <div className="relative z-10 animate-fadeIn">
            {activeTab === 'description' && <DescriptionTab description={product.description} />}
            {activeTab === 'specs' && <SpecsTab product={product} />}
            {activeTab === 'howToBuy' && <HowToBuyTab product={product} />}
            {activeTab === 'colors' && <ColorGalleryTab colors={product.colors || []} />}
            {activeTab === 'reviews' && (
              <div className="text-center py-8 sm:py-12">
                <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 rounded-full bg-[#F4F1EA] flex items-center justify-center">
                  <FaStar className="w-7 h-7 sm:w-10 sm:h-10 text-[#D9D9D9]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#292524] mb-1 sm:mb-2">بخش نظرات</h3>
                <p className="text-xs sm:text-sm text-[#79716B]">به زودی اضافه می‌شود</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== استایل‌ها ===== */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* جلوگیری از اسکرول افقی */
        .max-w-7xl {
          max-width: 100% !important;
          overflow-x: hidden !important;
        }
        
        /* تنظیم فونت در موبایل */
        @media (max-width: 640px) {
          .text-xs {
            font-size: 10px !important;
          }
          .text-sm {
            font-size: 12px !important;
          }
          .text-base {
            font-size: 14px !important;
          }
          .text-lg {
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetails;